import { useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "wouter";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Pencil,
  Plus,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { StandardTabsHeader } from "@/components/StandardTabsHeader";
import {
  useExpedientesList,
  useRulesCrud,
  useRulesForStandard,
  useStandard,
  useCandidates,
} from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import type { Rule, RuleFamily, RuleOperator, RuleSeverity } from "@/data/seed";
import {
  buildContextForExpediente,
  evaluateRules,
  ruleFamilyMeta,
  ruleFieldCatalog,
  ruleOperators,
  suggestRules,
} from "@/lib/ai/simulate";

const families: RuleFamily[] = ["RG", "RE", "RL", "RT", "RP", "RV", "RD"];

function nid() {
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function fieldDef(field: string) {
  return ruleFieldCatalog.find((f) => f.field === field) ?? ruleFieldCatalog[0];
}

function readableCondition(rule: Rule): string {
  const def = fieldDef(rule.condicion.field);
  const unit = rule.condicion.unit ? ` ${rule.condicion.unit}` : "";
  return `${def.label} ${rule.condicion.operator} ${String(rule.condicion.value)}${unit}`;
}

export default function DictaminationRules() {
  const params = useParams<{ codigo: string }>();
  const codigo = params.codigo ?? "EC1781";
  const standard = useStandard(codigo);
  const rules = useRulesForStandard(codigo);
  const { setAll, upsert, remove } = useRulesCrud(codigo);
  const expedientes = useExpedientesList().filter((e) => e.estandar === codigo);
  const candidates = useCandidates();
  const logEvent = useDemoStore((s) => s.logEvent);
  const activeRole = useDemoStore((s) => s.activeRole);

  const [activeFamily, setActiveFamily] = useState<RuleFamily>("RG");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Rule>>({});
  const [adding, setAdding] = useState(false);
  const [aiPreview, setAiPreview] = useState<Rule[] | null>(null);
  const [aiSelected, setAiSelected] = useState<Record<string, boolean>>({});
  const [selectedExpId, setSelectedExpId] = useState<string>(expedientes[0]?.id ?? "");

  const exp = expedientes.find((e) => e.id === selectedExpId) ?? expedientes[0];
  const context = useMemo(() => {
    if (!exp) return null;
    return buildContextForExpediente(exp, {
      standardEstado: standard?.estado,
      candidato: candidates[exp.candidatoId],
    });
  }, [exp, standard?.estado, candidates]);

  const effectiveRules = useMemo<Rule[]>(() => {
    if (!editingId && !adding) return rules;
    const draftRule: Rule | null = (() => {
      const c = draft.condicion;
      if (!c?.field || !draft.nombre) return null;
      return {
        id: editingId ?? "draft-new",
        estandar: codigo,
        familia: (draft.familia as RuleFamily) ?? activeFamily,
        nombre: String(draft.nombre),
        descripcion: String(draft.descripcion ?? ""),
        severidad: (draft.severidad as RuleSeverity) ?? "bloqueante",
        condicion: {
          field: String(c.field),
          operator: (c.operator as RuleOperator) ?? ">=",
          value: c.value as Rule["condicion"]["value"],
          unit: c.unit,
        },
        habilitada: draft.habilitada ?? true,
      };
    })();
    if (!draftRule) return rules;
    if (editingId) return rules.map((r) => (r.id === editingId ? draftRule : r));
    return [...rules, draftRule];
  }, [rules, draft, editingId, adding, codigo, activeFamily]);

  const allResults = useMemo(
    () => (context ? evaluateRules(effectiveRules, context) : []),
    [effectiveRules, context],
  );
  const resultByRule = useMemo(() => {
    const m = new Map<string, boolean>();
    allResults.forEach((r) => m.set(r.rule.id, r.passed));
    return m;
  }, [allResults]);

  const familyStatus = useMemo(() => {
    const status: Record<RuleFamily, "ok" | "warn" | "fail" | "empty"> = {
      RG: "empty", RE: "empty", RL: "empty", RT: "empty",
      RP: "empty", RV: "empty", RD: "empty",
    };
    for (const f of families) {
      const fam = effectiveRules.filter((r) => r.familia === f);
      if (fam.length === 0) { status[f] = "empty"; continue; }
      const failed = fam.filter((r) => r.habilitada && resultByRule.get(r.id) === false);
      if (failed.length === 0) status[f] = "ok";
      else if (failed.some((r) => r.severidad === "bloqueante")) status[f] = "fail";
      else status[f] = "warn";
    }
    return status;
  }, [effectiveRules, resultByRule]);

  const familyRules = rules.filter((r) => r.familia === activeFamily);

  function log(accion: string, id: string) {
    logEvent({ actor: "Administrador académico", rol: activeRole, accion, entidad: "rule", entidadId: id });
  }

  function startEdit(r: Rule) {
    setEditingId(r.id);
    setDraft({ ...r, condicion: { ...r.condicion } });
    setAdding(false);
  }
  function cancelEdit() {
    setEditingId(null);
    setDraft({});
    setAdding(false);
  }
  function saveDraft() {
    if (!draft.nombre || !draft.condicion?.field) return;
    const next: Rule = {
      id: editingId ?? nid(),
      estandar: codigo,
      familia: (draft.familia as RuleFamily) ?? activeFamily,
      nombre: String(draft.nombre),
      descripcion: String(draft.descripcion ?? ""),
      severidad: (draft.severidad as RuleSeverity) ?? "bloqueante",
      condicion: {
        field: String(draft.condicion.field),
        operator: (draft.condicion.operator as RuleOperator) ?? ">=",
        value: draft.condicion.value as Rule["condicion"]["value"],
        unit: draft.condicion.unit,
      },
      habilitada: draft.habilitada ?? true,
    };
    upsert(next);
    log(editingId ? "editar-regla" : "crear-regla", next.id);
    cancelEdit();
  }
  function deleteRule(r: Rule) {
    if (!confirm(`¿Eliminar regla "${r.nombre}"?`)) return;
    remove(r.id);
    log("eliminar-regla", r.id);
  }
  function startAdd() {
    setAdding(true);
    setEditingId(null);
    setDraft({
      familia: activeFamily,
      severidad: "bloqueante",
      habilitada: true,
      nombre: "",
      descripcion: "",
      condicion: { ...ruleFieldCatalog[0], operator: ">=", value: ruleFieldCatalog[0].type === "boolean" ? true : 0 },
    });
  }
  function toggleEnabled(r: Rule) {
    upsert({ ...r, habilitada: !r.habilitada });
    log("toggle-regla", r.id);
  }
  function runAiSuggest() {
    const proposed = suggestRules({ codigo });
    const existingIds = new Set(rules.map((r) => r.id));
    const novel = proposed.filter((p) => !existingIds.has(p.id));
    setAiPreview(novel);
    setAiSelected(Object.fromEntries(novel.map((p) => [p.id, true])));
  }
  function applyAiSelected() {
    if (!aiPreview) return;
    aiPreview.filter((p) => aiSelected[p.id]).forEach((p) => upsert(p));
    log("aplicar-ia", `${aiPreview.length}-reglas`);
    setAiPreview(null);
    setAiSelected({});
  }
  function resetToSuggested() {
    if (!confirm("¿Restablecer todas las reglas al set sugerido por IA? Se perderán las personalizaciones.")) return;
    setAll(suggestRules({ codigo }));
    log("restablecer-reglas", codigo);
  }

  const totalActive = rules.filter((r) => r.habilitada).length;

  if (!standard) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo, "Reglas de dictamen"]}>
        <Card>
          <SectionTitle>Estándar no encontrado</SectionTitle>
          <Link href="/admin/estandares" data-testid="link-back-list">
            <PrimaryButton variant="outline">Volver al catálogo</PrimaryButton>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb={["Estándares", codigo, "Reglas de dictamen"]}>
      <StandardTabsHeader
        standard={standard}
        active="reglas"
        rightAction={
          <>
            <PrimaryButton variant="outline" onClick={resetToSuggested} testId="button-reset-rules"><RotateCcw size={13} /> Restablecer reglas</PrimaryButton>
            <PrimaryButton variant="outline" onClick={runAiSuggest} testId="button-ai-rules"><Sparkles size={13} /> Sugerir reglas con IA</PrimaryButton>
            <Link href={`/admin/estandares/${codigo}/publicar`} data-testid="link-next-publish">
              <PrimaryButton>Continuar a publicación <ArrowRight size={14} /></PrimaryButton>
            </Link>
          </>
        }
      />

      <div style={{ marginBottom: 14, fontSize: 13, color: "var(--cc-gray-600)" }}>
        <strong style={{ color: "var(--cc-navy)" }}>{families.length}</strong> familias · <strong style={{ color: "var(--cc-navy)" }}>{totalActive}</strong> reglas activas · simulador en vivo
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 320px", gap: 14 }}>
        {/* Sidebar of families */}
        <Card padding={0}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cc-gray-100)", fontWeight: 700, fontSize: 13, color: "var(--cc-navy)" }}>
            Familias
          </div>
          {families.map((f) => {
            const meta = ruleFamilyMeta[f];
            const status = familyStatus[f];
            const count = rules.filter((r) => r.familia === f).length;
            const active = activeFamily === f;
            return (
              <button
                key={f}
                onClick={() => setActiveFamily(f)}
                data-testid={`family-${f}`}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  border: "none",
                  borderLeft: active ? "3px solid var(--cc-teal)" : "3px solid transparent",
                  background: active ? "var(--cc-gray-50)" : "white",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: statusColor(status) }} />
                <span style={{ width: 28, height: 22, borderRadius: 5, background: meta.color, color: "white", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{f}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)", lineHeight: 1.2 }}>{meta.label}</div>
                  <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>{count} reglas</div>
                </div>
              </button>
            );
          })}
        </Card>

        {/* Main editor */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card padding={0}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>
                  {ruleFamilyMeta[activeFamily].label} <Badge tone="teal">{familyRules.length}</Badge>
                </div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>
                  Editables in-place. Cambios aplican inmediatamente al dictaminador.
                </div>
              </div>
              <PrimaryButton size="sm" variant="outline" onClick={startAdd} testId="button-add-rule"><Plus size={12} /> Nueva regla</PrimaryButton>
            </div>
            <div style={{ padding: 12 }}>
              {familyRules.length === 0 && !adding && (
                <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: "12px 6px" }}>Sin reglas en esta familia.</div>
              )}
              {familyRules.map((r) => {
                const passed = resultByRule.get(r.id);
                const isEditing = editingId === r.id;
                return (
                  <div key={r.id} data-testid={`row-rule-${r.id}`} style={ruleRow}>
                    {isEditing ? (
                      <RuleEditor draft={draft} setDraft={setDraft} onCancel={cancelEdit} onSave={saveDraft} />
                    ) : (
                      <>
                        <button onClick={() => toggleEnabled(r)} data-testid={`toggle-${r.id}`} title={r.habilitada ? "Activa" : "Inactiva"} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
                          {r.habilitada
                            ? <CheckCircle2 size={18} color="var(--cc-teal)" />
                            : <XCircle size={18} color="var(--cc-gray-300)" />}
                        </button>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>{r.nombre}</span>
                            <Badge tone={r.severidad === "bloqueante" ? "red" : "amber"}>{r.severidad}</Badge>
                            {passed === true && <Badge tone="green">Pasa</Badge>}
                            {passed === false && <Badge tone="red">Falla</Badge>}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>{r.descripcion}</div>
                          <div style={{ fontSize: 11, color: "var(--cc-gray-700)", marginTop: 4, fontFamily: "ui-monospace, monospace" }}>
                            {readableCondition(r)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => startEdit(r)} data-testid={`edit-${r.id}`} style={miniBtn}><Pencil size={12} /></button>
                          <button onClick={() => deleteRule(r)} data-testid={`del-${r.id}`} style={miniBtn}><Trash2 size={12} /></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {adding && (
                <div data-testid="row-rule-new" style={ruleRow}>
                  <RuleEditor draft={draft} setDraft={setDraft} onCancel={cancelEdit} onSave={saveDraft} />
                </div>
              )}
            </div>
          </Card>

          {aiPreview && (
            <Card style={{ background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Sparkles size={14} color="var(--cc-teal)" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", textTransform: "uppercase" }}>Sugerencias IA · {aiPreview.length} reglas nuevas</span>
              </div>
              {aiPreview.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>El estándar ya tiene todas las reglas sugeridas.</div>
              ) : (
                <>
                  {aiPreview.map((p) => (
                    <label key={p.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: 6, fontSize: 12 }}>
                      <input type="checkbox" checked={!!aiSelected[p.id]} onChange={(e) => setAiSelected({ ...aiSelected, [p.id]: e.target.checked })} />
                      <div>
                        <div style={{ fontWeight: 700, color: "var(--cc-navy)" }}>[{p.familia}] {p.nombre}</div>
                        <div style={{ color: "var(--cc-gray-600)" }}>{readableCondition(p)} · {p.severidad}</div>
                      </div>
                    </label>
                  ))}
                  <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <PrimaryButton size="sm" variant="ghost" onClick={() => { setAiPreview(null); setAiSelected({}); }}>Descartar</PrimaryButton>
                    <PrimaryButton size="sm" onClick={applyAiSelected} testId="apply-ai-rules">Aceptar selección</PrimaryButton>
                  </div>
                </>
              )}
            </Card>
          )}
        </div>

        {/* Simulator */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <ShieldCheck size={16} color="var(--cc-teal)" />
              <SectionTitle>Simulador en vivo</SectionTitle>
            </div>
            {expedientes.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Sin expedientes para este estándar.</div>
            ) : (
              <>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-600)" }}>Expediente</label>
                <select
                  value={selectedExpId}
                  onChange={(e) => setSelectedExpId(e.target.value)}
                  data-testid="select-sim-exp"
                  style={{ ...inputStyle, marginTop: 4, marginBottom: 10 }}
                >
                  {expedientes.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.id.toUpperCase()} · {candidates[e.candidatoId]?.iniciales ?? "?"}
                    </option>
                  ))}
                </select>
                <SimSummary results={allResults} />
                <div style={{ maxHeight: 360, overflowY: "auto", marginTop: 10 }}>
                  {allResults.map((res) => (
                    <div key={res.rule.id} style={{ padding: "8px 6px", borderBottom: "1px solid var(--cc-gray-100)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      {res.passed
                        ? <Check size={14} color="var(--cc-green)" style={{ flexShrink: 0, marginTop: 2 }} />
                        : <X size={14} color="var(--cc-red)" style={{ flexShrink: 0, marginTop: 2 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-navy)" }}>
                          [{res.rule.familia}] {res.rule.nombre}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--cc-gray-500)", fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
                          {res.detalle}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function SimSummary({ results }: { results: { rule: Rule; passed: boolean }[] }) {
  const total = results.length;
  const pass = results.filter((r) => r.passed).length;
  const blockFail = results.filter((r) => !r.passed && r.rule.severidad === "bloqueante").length;
  const warnFail = results.filter((r) => !r.passed && r.rule.severidad === "advertencia").length;
  const decision = blockFail === 0 ? "Competente" : "Todavía no competente";
  return (
    <div style={{ padding: 10, borderRadius: 8, background: blockFail === 0 ? "var(--cc-green-bg)" : "var(--cc-red-bg)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: blockFail === 0 ? "var(--cc-green)" : "var(--cc-red)", textTransform: "uppercase" }}>
        Recomendación: {decision}
      </div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-700)", marginTop: 4 }}>
        {pass}/{total} reglas pasan · {blockFail} bloqueantes · {warnFail} advertencias
      </div>
    </div>
  );
}

function RuleEditor({
  draft, setDraft, onSave, onCancel,
}: {
  draft: Partial<Rule>;
  setDraft: (d: Partial<Rule>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const cond = draft.condicion ?? { field: ruleFieldCatalog[0].field, operator: ">=", value: 0 };
  const def = fieldDef(cond.field);
  return (
    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 130px", gap: 8 }}>
        <input
          autoFocus
          value={String(draft.nombre ?? "")}
          onChange={(e) => setDraft({ ...draft, nombre: e.target.value })}
          placeholder="Nombre de la regla"
          data-testid="edit-rule-name"
          style={inputStyle}
        />
        <select
          value={String(draft.familia ?? "RG")}
          onChange={(e) => setDraft({ ...draft, familia: e.target.value as RuleFamily })}
          data-testid="edit-rule-family"
          style={inputStyle}
        >
          {families.map((f) => <option key={f} value={f}>{f} · {ruleFamilyMeta[f].label}</option>)}
        </select>
        <select
          value={String(draft.severidad ?? "bloqueante")}
          onChange={(e) => setDraft({ ...draft, severidad: e.target.value as RuleSeverity })}
          data-testid="edit-rule-severity"
          style={inputStyle}
        >
          <option value="bloqueante">Bloqueante</option>
          <option value="advertencia">Advertencia</option>
        </select>
      </div>
      <input
        value={String(draft.descripcion ?? "")}
        onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
        placeholder="Descripción"
        data-testid="edit-rule-desc"
        style={inputStyle}
      />
      <div style={{ display: "grid", gridTemplateColumns: "2fr 90px 1fr 100px auto", gap: 6, alignItems: "center" }}>
        <select
          value={cond.field}
          onChange={(e) => {
            const next = fieldDef(e.target.value);
            const value = next.type === "boolean" ? true : next.type === "string" ? (next.enumValues?.[0] ?? "") : 0;
            const op: RuleOperator = next.type === "number" ? (cond.operator ?? ">=") : "==";
            setDraft({ ...draft, condicion: { field: next.field, operator: op, value, unit: next.unit } });
          }}
          data-testid="edit-rule-field"
          style={inputStyle}
        >
          {ruleFieldCatalog.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
        </select>
        <select
          value={cond.operator}
          onChange={(e) => setDraft({ ...draft, condicion: { ...cond, operator: e.target.value as RuleOperator } })}
          data-testid="edit-rule-op"
          style={inputStyle}
        >
          {(def.type === "number" ? ruleOperators : (["==", "!="] as RuleOperator[])).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {def.type === "boolean" ? (
          <select
            value={String(cond.value)}
            onChange={(e) => setDraft({ ...draft, condicion: { ...cond, value: e.target.value === "true" } })}
            data-testid="edit-rule-val"
            style={inputStyle}
          >
            <option value="true">verdadero</option>
            <option value="false">falso</option>
          </select>
        ) : def.type === "string" ? (
          <select
            value={String(cond.value)}
            onChange={(e) => setDraft({ ...draft, condicion: { ...cond, value: e.target.value } })}
            data-testid="edit-rule-val"
            style={inputStyle}
          >
            {(def.enumValues ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        ) : (
          <input
            type="number"
            value={Number(cond.value as number)}
            onChange={(e) => setDraft({ ...draft, condicion: { ...cond, value: Number(e.target.value) } })}
            data-testid="edit-rule-val"
            style={inputStyle}
          />
        )}
        <input
          value={String(cond.unit ?? def.unit ?? "")}
          onChange={(e) => setDraft({ ...draft, condicion: { ...cond, unit: e.target.value || undefined } })}
          placeholder="unidad"
          style={inputStyle}
        />
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={onSave} data-testid="save-rule" style={{ ...miniBtn, background: "var(--cc-navy)", color: "white", borderColor: "var(--cc-navy)" }}><Check size={12} /></button>
          <button onClick={onCancel} style={miniBtn}><X size={12} /></button>
        </div>
      </div>
    </div>
  );
}

function statusColor(s: "ok" | "warn" | "fail" | "empty"): string {
  if (s === "ok") return "var(--cc-green)";
  if (s === "warn") return "var(--cc-amber)";
  if (s === "fail") return "var(--cc-red)";
  return "var(--cc-gray-300)";
}

const ruleRow: CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: "10px 8px",
  borderBottom: "1px solid var(--cc-gray-100)",
};

const miniBtn: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 6,
  border: "1px solid var(--cc-gray-200)",
  background: "white",
  color: "var(--cc-gray-600)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 6,
  fontSize: 12,
  fontFamily: "inherit",
  background: "white",
};
